import React, { useEffect, useRef, useState } from "react";
import { Search, Loader2, Check } from "lucide-react";
import api from "@/lib/api";

// Autocomplete that suggests HS codes as the user types a code, product title
// or description. On select, reports the code + standard unit of measure.
export default function HsCodeField({ value = "", onChange, onSelect, testid = "hs-code", placeholder }) {
  const [query, setQuery] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(-1);
  const boxRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => { setQuery(value); }, [value]);

  useEffect(() => {
    const onDocClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const runSearch = (q) => {
    clearTimeout(timer.current);
    if (!q || q.trim().length < 2) { setSuggestions([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.get(`/hs/suggest`, { params: { q, limit: 8 } });
        setSuggestions(res.data);
        setOpen(res.data.length > 0);
        setActive(-1);
      } catch (e) { setSuggestions([]); }
      finally { setLoading(false); }
    }, 220);
  };

  const handleInput = (e) => {
    const v = e.target.value;
    setQuery(v);
    onChange && onChange(v);
    runSearch(v);
  };

  const pick = (s) => {
    setQuery(s.code);
    onChange && onChange(s.code);
    onSelect && onSelect(s);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter" && active >= 0) { e.preventDefault(); pick(suggestions[active]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div className="relative" ref={boxRef}>
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9AA6AE]" aria-hidden="true" />
        <input
          value={query}
          onChange={handleInput}
          onFocus={() => query.length >= 2 && suggestions.length > 0 && setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder || "Type a code, product title or description"}
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          data-testid={testid}
          className="w-full min-h-[48px] rounded-md border border-[#D9E0E4] bg-white pl-10 pr-9 text-[15px] text-navy placeholder:text-[#9AA6AE] focus:outline-none focus:ring-2 focus:ring-teal focus:border-teal transition-colors"
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-teal animate-spin" />}
      </div>

      {open && (
        <ul
          className="absolute z-30 mt-1 w-full max-h-72 overflow-y-auto rounded-md border border-[#D9E0E4] bg-white shadow-xl py-1"
          role="listbox"
          data-testid={`${testid}-suggestions`}
        >
          {suggestions.map((s, i) => (
            <li key={s.code}>
              <button
                type="button"
                onMouseEnter={() => setActive(i)}
                onClick={() => pick(s)}
                data-testid={`${testid}-option-${s.code}`}
                className={`w-full text-left px-3.5 py-2.5 flex items-start gap-3 ${active === i ? "bg-[#EAF1F4]" : "hover:bg-[#EAF1F4]"}`}
              >
                <span className="font-heading font-bold text-teal text-[13px] mt-0.5 shrink-0 w-16">{s.code}</span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[13.5px] text-navy leading-snug">{s.description}</span>
                  <span className="text-[12px] text-muted-foreground">Unit: {s.unit}</span>
                </span>
                {query === s.code && <Check className="h-4 w-4 text-teal shrink-0" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
